import { useEffect, useMemo, useState } from 'react';
import BanquetHelpPanel from './BanquetHelpPanel';

function BanquetCombinationMapMaster({ token, selectedProperty, selectedOutletId = '', showHelp = false, onOpenFaq }) {
  const chainId = String(selectedProperty?.chain_id || '').trim();
  const propertyId = String(selectedProperty?.id || '').trim();
  const outletId = String(selectedOutletId || '').trim();
  const headers = useMemo(
    () => ({
      Authorization: `Bearer ${token}`,
      'x-chain-id': chainId,
      'x-property-id': propertyId,
      ...(outletId ? { 'x-outlet-id': outletId } : {})
    }),
    [token, chainId, propertyId, outletId]
  );

  const [combos, setCombos] = useState([]);
  const [partitions, setPartitions] = useState([]);
  const [rows, setRows] = useState([]);
  const [error, setError] = useState('');
  const [form, setForm] = useState({ combo_id: '', partition_id: '' });

  const parseJson = async (res) => {
    const text = await res.text();
    try {
      const json = text ? JSON.parse(text) : {};
      return { data: json, error: json?.error || '' };
    } catch {
      return { data: null, error: text || '' };
    }
  };

  const loadCombos = async () => {
    const res = await fetch('/api/v1/banquet/combinations', { headers });
    const { data, error: apiError } = await parseJson(res);
    if (!res.ok) return setError(apiError || 'Failed to load combinations');
    const list = Array.isArray(data) ? data : [];
    setCombos(list);
    if (!form.combo_id && list.length) setForm((prev) => ({ ...prev, combo_id: String(list[0].combo_id) }));
  };

  const loadPartitions = async () => {
    const res = await fetch('/api/v1/banquet/partitions', { headers });
    const { data, error: apiError } = await parseJson(res);
    if (!res.ok) return setError(apiError || 'Failed to load partitions');
    const list = Array.isArray(data) ? data : [];
    setPartitions(list);
    if (!form.partition_id && list.length) setForm((prev) => ({ ...prev, partition_id: String(list[0].partition_id) }));
  };

  const loadRows = async () => {
    const q = new URLSearchParams();
    if (form.combo_id) q.set('combo_id', form.combo_id);
    const res = await fetch(`/api/v1/banquet/combination-maps?${q.toString()}`, { headers });
    const { data, error: apiError } = await parseJson(res);
    if (!res.ok) return setError(apiError || 'Failed to load combination maps');
    setRows(Array.isArray(data) ? data : []);
  };

  useEffect(() => {
    if (!chainId || !propertyId) return;
    Promise.all([loadCombos(), loadPartitions()]).then(loadRows).catch((err) => setError(err?.message || 'Failed to load map data'));
  }, [chainId, propertyId, outletId]);

  useEffect(() => {
    if (chainId && propertyId) loadRows().catch((err) => setError(err?.message || 'Failed to load maps'));
  }, [form.combo_id]);

  const addMap = async () => {
    setError('');
    if (!form.combo_id || !form.partition_id) return setError('Combination and partition are required');
    const res = await fetch('/api/v1/banquet/combination-maps', {
      method: 'POST',
      headers: { ...headers, 'Content-Type': 'application/json' },
      body: JSON.stringify({ combo_id: Number(form.combo_id), partition_id: Number(form.partition_id) })
    });
    const { error: apiError } = await parseJson(res);
    if (!res.ok) return setError(apiError || 'Add map failed');
    loadRows();
    loadCombos();
  };

  const removeMap = async (mapId) => {
    setError('');
    const res = await fetch(`/api/v1/banquet/combination-maps/${mapId}`, { method: 'DELETE', headers });
    const { error: apiError } = await parseJson(res);
    if (!res.ok) return setError(apiError || 'Delete map failed');
    loadRows();
    loadCombos();
  };

  return (
    <div className="page-shell">
      <h2 style={{ color: 'var(--theme-primary)' }}>Banquet - Combination Map Master</h2>
      <BanquetHelpPanel visible={showHelp} onOpenFaq={onOpenFaq} />
      {error ? <div className="error-banner">{error}</div> : null}

      <div className="filter-bar" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(180px,1fr))', gap: '0.6rem' }}>
        <select value={form.combo_id} onChange={(e) => setForm({ ...form, combo_id: e.target.value })}>
          <option value="">Select combination</option>
          {combos.map((c) => <option key={c.combo_id} value={c.combo_id}>{c.combo_name} (Cap {c.total_capacity})</option>)}
        </select>
        <select value={form.partition_id} onChange={(e) => setForm({ ...form, partition_id: e.target.value })}>
          <option value="">Select partition</option>
          {partitions.map((p) => <option key={p.partition_id} value={p.partition_id}>{p.parent_hall_name} - {p.partition_name} ({p.capacity})</option>)}
        </select>
        <button onClick={addMap}>Add Mapping</button>
      </div>

      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', background: '#fff', border: '1px solid var(--theme-border)' }}>
          <thead>
            <tr style={{ background: '#e6f0fa' }}>
              <th style={{ textAlign: 'left' }}>Combination</th>
              <th style={{ textAlign: 'left' }}>Partition</th>
              <th style={{ textAlign: 'right' }}>Partition Capacity</th>
              <th style={{ textAlign: 'left' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.map_id}>
                <td>{r.combo_name}</td>
                <td>{r.partition_name}</td>
                <td style={{ textAlign: 'right' }}>{r.capacity}</td>
                <td><button onClick={() => removeMap(r.map_id)}>Remove</button></td>
              </tr>
            ))}
            {!rows.length ? <tr><td colSpan={4}>No mappings found.</td></tr> : null}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default BanquetCombinationMapMaster;
